/* eslint-disable @typescript-eslint/no-deprecated */
import { z } from 'zod/v4';
import { stringToBooleanSchema } from '../common/index';

const multiCurrencyPricesSchema = z
  .record(z.string().length(3), z.number().min(0))
  .refine((prices) => Object.keys(prices).length > 0, {
    message: 'At least one price is required',
  });

/**
 * Полная валидация промо-цены тарифа.
 * Промо `0` допустимо — это «временно бесплатно» на срок акции.
 * Истёкшее `promoUntil` сохранять разрешено (просто неактивно), потому дату в прошлом не блокируем.
 */
type PromoValidatable = {
  prices?: Record<string, number>;
  promoPrices?: Record<string, number> | null;
  promoUntil?: string | null;
};

function validatePromo(data: PromoValidatable, ctx: z.RefinementCtx): void {
  const hasPromoPrices = data.promoPrices !== null && data.promoPrices !== undefined && Object.keys(data.promoPrices).length > 0;
  const hasPromoUntil = data.promoUntil !== null && data.promoUntil !== undefined && data.promoUntil !== '';

  // 1. promoPrices и promoUntil задаются только вместе
  if (hasPromoPrices !== hasPromoUntil) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [hasPromoPrices ? 'promoUntil' : 'promoPrices'],
      message: 'Акционная цена и срок акции должны быть заданы вместе',
    });
    return;
  }

  if (!hasPromoPrices || data.promoPrices === null || data.promoPrices === undefined) {
    return;
  }

  // Для update-схемы prices может отсутствовать — тогда кросс-проверки с базовой ценой пропускаем
  const prices = data.prices;
  if (prices === null || prices === undefined) {
    return;
  }

  for (const [currency, promoValue] of Object.entries(data.promoPrices)) {
    // 2. Валюта промо должна присутствовать в базовых ценах
    if (!Object.hasOwn(prices, currency)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['promoPrices', currency],
        message: `Валюта ${currency} отсутствует в основных ценах`,
      });
      continue;
    }
    // 3. Промо-цена не должна быть дороже основной
    if (promoValue > prices[currency]) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['promoPrices', currency],
        message: `Акционная цена (${promoValue}) не может быть выше основной (${prices[currency]})`,
      });
    }
  }
}

const hexColorSchema = z.string().regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/, {
  message: 'Цвет должен быть в формате #RGB, #RRGGBB или #RRGGBBAA',
});

const cardThemeColorsSchema = z.object({
  bg: hexColorSchema,
  patternPrimary: hexColorSchema,
  patternSecondary: hexColorSchema,
});

const cardAppearanceSchema = z.object({
  light: cardThemeColorsSchema,
  dark: cardThemeColorsSchema,
}).nullable();

const bonusTemplateBaseSchema = z.object({
  type: z.enum(['NUMBER', 'BOOLEAN', 'ARRAY', 'STRING']),
  initialValue: z.union([z.number().min(0), z.boolean(), z.array(z.string()), z.string()]),
  durationMode: z.enum(['RELATIVE', 'ABSOLUTE', 'INHERIT']).default('INHERIT'),
  durationDays: z.number().int().positive().nullish(),
  expiresAt: z.string().datetime().nullish(),
  order: z.number().int().min(0).default(0),
  translations: z
    .array(
      z.object({
        languageCode: z.string().min(2).max(5),
        name: z.string().min(1).max(200),
        description: z.string().max(1000).nullish(),
      }),
    )
    .optional(),
});

const bonusTemplateSchema = bonusTemplateBaseSchema
  .refine(
    (data) => {
      switch (data.type) {
        case 'NUMBER':
          return typeof data.initialValue === 'number';
        case 'BOOLEAN':
          return typeof data.initialValue === 'boolean';
        case 'ARRAY':
          return Array.isArray(data.initialValue);
        case 'STRING':
          return typeof data.initialValue === 'string';
        default:
          return false;
      }
    },
    {
      message:
        'initialValue must match type: NUMBER→number, BOOLEAN→boolean, ARRAY→string[], STRING→string',
    },
  )
  .refine(
    (data) => {
      if (data.durationMode === 'RELATIVE') {
        return data.durationDays !== null && data.durationDays !== undefined && data.durationDays > 0;
      }
      return true;
    },
    {
      message: 'durationDays is required when durationMode is RELATIVE',
    },
  )
  .refine(
    (data) => {
      if (data.durationMode === 'ABSOLUTE') {
        return data.expiresAt !== null && data.expiresAt !== undefined;
      }
      return true;
    },
    {
      message: 'expiresAt is required when durationMode is ABSOLUTE',
    },
  );

export const createPricingTierSchema = z
  .object({
    // Exactly one must be provided
    channelPassId: z.number().int().positive().nullish(),
    courseId: z.number().int().positive().nullish(),
    subscriptionPlanId: z.number().int().positive().nullish(),
    bookId: z.number().int().positive().nullish(),

    durationDays: z.number().int().positive().nullish(),
    prices: multiCurrencyPricesSchema,
    baseCurrency: z.string().length(3).default('UZS'),

    // Промо-цены (временная акция)
    promoPrices: z.record(z.string().length(3), z.number().min(0)).nullish(),
    promoUntil: z.string().datetime().nullish(),

    isActive: z.boolean().default(true),
    order: z.number().int().min(0).default(0),

    // Семейный тариф
    isFamily: z.boolean().default(false),
    familySlots: z.number().int().min(0).default(0),

    // Изображение
    imageId: z.number().int().positive().nullable().optional(),

    // Градиент для UI
    gradientFrom: z.string().nullable().optional(),
    gradientTo: z.string().nullable().optional(),
    gradientAngle: z.number().int().min(0).max(360).optional(),

    // Внешний вид карточки абонемента (light/dark), null = дефолтные цвета на платформе
    cardAppearance: cardAppearanceSchema.optional(),

    // Целевая языковая аудитория (пустой массив = видно всем)
    targetLanguages: z.array(z.string().min(2).max(5)).default([]),

    translations: z
      .array(
        z.object({
          languageCode: z.string().min(2).max(5),
          name: z.string().min(1).max(200),
          shortDescription: z.string().max(500).nullish(),
        }),
      )
      .optional(),

    bonusTemplates: z.array(bonusTemplateSchema).optional(),
  })
  .refine(
    (data) => {
      const count = [data.channelPassId, data.courseId, data.subscriptionPlanId, data.bookId].filter(
        (v) => v !== null && v !== undefined,
      ).length;
      return count === 1;
    },
    {
      message: 'Exactly one of channelPassId, courseId, subscriptionPlanId, or bookId must be provided',
    },
  )
  .refine(
    (data) => {
      return Object.hasOwn(data.prices, data.baseCurrency);
    },
    {
      message: 'baseCurrency must be a key in prices',
    },
  )
  .superRefine(validatePromo);

export const updatePricingTierSchema = z.object({
  durationDays: z.number().int().positive().nullish(),
  prices: multiCurrencyPricesSchema.optional(),
  baseCurrency: z.string().length(3).optional(),

  // Промо-цены
  promoPrices: z.record(z.string().length(3), z.number().min(0)).nullish(),
  promoUntil: z.string().datetime().nullish(),

  isActive: z.boolean().optional(),
  order: z.number().int().min(0).optional(),

  // Семейный тариф
  isFamily: z.boolean().optional(),
  familySlots: z.number().int().min(0).optional(),

  // Изображение
  imageId: z.number().int().positive().nullable().optional(),

  // Градиент для UI
  gradientFrom: z.string().nullable().optional(),
  gradientTo: z.string().nullable().optional(),
  gradientAngle: z.number().int().min(0).max(360).optional(),

  // Внешний вид карточки абонемента (light/dark), null = дефолтные цвета на платформе
  cardAppearance: cardAppearanceSchema.optional(),

  // Целевая языковая аудитория (пустой массив = видно всем)
  targetLanguages: z.array(z.string().min(2).max(5)).optional(),

  translations: z
    .array(
      z.object({
        languageCode: z.string().min(2).max(5),
        name: z.string().min(1).max(200),
        shortDescription: z.string().max(500).nullish(),
      }),
    )
    .optional(),

  // Применить изменения durationDays/familySlots/isFamily к существующим активным подпискам
  applyToExisting: z.boolean().optional(),
}).superRefine(validatePromo);

export const pricingTierFiltersSchema = z.object({
  channelPassId: z.coerce.number().int().positive().optional(),
  courseId: z.coerce.number().int().positive().optional(),
  subscriptionPlanId: z.coerce.number().int().positive().optional(),
  bookId: z.coerce.number().int().positive().optional(),
  isActive: stringToBooleanSchema,
});

export const createBonusTemplateSchema = bonusTemplateSchema;

export const updateBonusTemplateSchema = bonusTemplateBaseSchema.partial().extend({
  applyToExisting: z.boolean().optional(),
});

export const reorderPricingTiersSchema = z.object({
  ids: z.array(z.number().int().positive()),
});

export type CreatePricingTierInput = z.infer<typeof createPricingTierSchema>;
export type UpdatePricingTierInput = z.infer<typeof updatePricingTierSchema>;
export type PricingTierFilters = z.infer<typeof pricingTierFiltersSchema>;
export type CreateBonusTemplateInput = z.infer<typeof createBonusTemplateSchema>;
export type UpdateBonusTemplateInput = z.infer<typeof updateBonusTemplateSchema>;

// ============================================================================
// PricingTierEntitlement — доступы тарифа (specific / quantity)
// ============================================================================

export const entitlementTargetTypeSchema = z.enum(['COURSE', 'CHANNEL', 'BOOK']);
export const entitlementSelectionModeSchema = z.enum(['specific', 'quantity']);
export const entitlementCourseTypeSchema = z.enum(['ONLINE', 'MEETING', 'OFFLINE']);

const baseEntitlementSchema = z.object({
  targetType: entitlementTargetTypeSchema,
  targetId: z.number().int().positive().nullish(),
  selectionMode: entitlementSelectionModeSchema,
  quantity: z.number().int().positive().nullish(),
  filterCourseType: entitlementCourseTypeSchema.nullish(),
  accessDurationDays: z.number().int().positive().nullish(),
  isActive: z.boolean().optional(),
  order: z.number().int().nonnegative().optional(),
});

export const createPricingTierEntitlementSchema = baseEntitlementSchema.refine(
  (v) => {
    if (v.selectionMode === 'specific') {
      return v.targetId !== null && v.targetId !== undefined && (v.quantity === null || v.quantity === undefined);
    }
    return (v.targetId === null || v.targetId === undefined) && v.quantity !== null && v.quantity !== undefined && v.quantity > 0;
  },
  { message: 'specific требует targetId; quantity требует quantity > 0 и не должен иметь targetId' },
).refine(
  (v) => v.filterCourseType === null || v.filterCourseType === undefined
    || (v.targetType === 'COURSE' && v.selectionMode === 'quantity'),
  { message: 'filterCourseType допустим только для COURSE+quantity' },
);

export const updatePricingTierEntitlementSchema = baseEntitlementSchema.partial().extend({
  applyToExisting: z.boolean().optional(),
}).refine(
  (v) => v.filterCourseType === null || v.filterCourseType === undefined
    || v.targetType === undefined
    || (v.targetType === 'COURSE' && v.selectionMode === 'quantity'),
  { message: 'filterCourseType допустим только для COURSE+quantity' },
);

export type CreatePricingTierEntitlementInput = z.infer<typeof createPricingTierEntitlementSchema>;
export type UpdatePricingTierEntitlementInput = z.infer<typeof updatePricingTierEntitlementSchema>;
